import { Box, Chip, LinearProgress, Stack, Tooltip, Typography } from '@mui/material';
import VerifiedIcon from '@mui/icons-material/Verified';
import GppBadIcon from '@mui/icons-material/GppBad';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';

/**
 * Read-only rendering for `license_verification` resource fields.
 *
 * These values are produced entirely by the mobile app's Jumio SDK scan and the
 * backend callback that follows it — the dashboard never captures or edits one,
 * it only displays what was captured. See the field type in
 * ResourceTypeManager and the value shape written by
 * VerificationEvaluationProvider.finalizeLicenseField in quik-core-v2.
 */

export interface LicenseImage {
  file_name?: string;
  file_path?: string;
  mime_type?: string;
  public_url?: string;
  signedUrl?: string;
}

export interface LicenseValue {
  type?: 'license_verification';
  provider?: string;
  /** 'processing' = scanned, awaiting Jumio's verdict. 'done' = verdict in. */
  status?: 'processing' | 'done';
  outcome?: 'true' | 'false' | 'other';
  result?: boolean;
  confidence?: number | null;
  message?: string | null;
  documentType?: string;
  extracted?: Record<string, any>;
  front?: LicenseImage | null;
  back?: LicenseImage | null;
  face?: LicenseImage | null;
  capturedAt?: string;
  verifiedAt?: string;
}

export const isLicenseValue = (v: any): v is LicenseValue =>
  !!v && typeof v === 'object' && !Array.isArray(v) && (v.type === 'license_verification' || !!v.verificationRef);

type Tone = { color: 'success' | 'error' | 'warning' | 'default'; label: string; icon: JSX.Element };

/** Verdict → chip presentation. `processing` is deliberately distinct from an
 *  'other' outcome: one is "not answered yet", the other is a final inconclusive. */
const toneFor = (value: LicenseValue): Tone => {
  if (value.status === 'processing') {
    return { color: 'warning', label: 'Verifying…', icon: <HourglassTopIcon fontSize="small" /> };
  }
  if (value.outcome === 'true') {
    return {
      color: 'success',
      label: value.confidence != null ? `Verified (${value.confidence}%)` : 'Verified',
      icon: <VerifiedIcon fontSize="small" />
    };
  }
  if (value.outcome === 'false') {
    return { color: 'error', label: 'Not verified', icon: <GppBadIcon fontSize="small" /> };
  }
  return { color: 'default', label: 'Inconclusive', icon: <HelpOutlineIcon fontSize="small" /> };
};

const imgSrc = (img?: LicenseImage | null): string | null => img?.signedUrl || img?.public_url || null;

const EXTRACTED_LABELS: Array<[string, string]> = [
  ['idNumber', 'Licence no.'],
  ['expiryDate', 'Expires'],
  ['issuingDate', 'Issued'],
  ['dob', 'Date of birth'],
  ['issuingCountry', 'Issuing country'],
  ['selectedDocumentType', 'Document type'],
  ['addressLine', 'Address'],
  ['city', 'City'],
  ['subdivision', 'State'],
  ['postCode', 'Post code']
];

const fullName = (e: Record<string, any> = {}): string =>
  [e.firstName, e.lastName].filter((v) => v != null && String(v).trim() !== '').join(' ');

/**
 * Compact one-line rendering for a table cell: verdict chip plus whichever of
 * the holder's name / licence number we managed to extract.
 */
export function LicenseVerificationCell({ value }: { value: LicenseValue }) {
  const tone = toneFor(value);
  const extracted = value.extracted || {};
  const name = fullName(extracted);
  const summary = [name, extracted.idNumber].filter(Boolean).join(' · ');

  return (
    <Tooltip title={value.message || ''} placement="top">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        <Chip size="small" color={tone.color} icon={tone.icon} label={tone.label} sx={{ flexShrink: 0 }} />
        {summary && (
          <Typography variant="caption" noWrap sx={{ color: 'text.secondary', minWidth: 0 }}>
            {summary}
          </Typography>
        )}
      </Stack>
    </Tooltip>
  );
}

/**
 * Full read-only panel: captured sides, verdict, confidence bar and every
 * extracted field we have. Used in the resource edit dialog and search results.
 */
export function LicenseVerificationPanel({ value, fieldName }: { value: LicenseValue; fieldName?: string }) {
  const tone = toneFor(value);
  const extracted = value.extracted || {};
  const name = fullName(extracted);
  const front = imgSrc(value.front);
  const back = imgSrc(value.back);
  const confidence = typeof value.confidence === 'number' ? value.confidence : null;

  const rows = EXTRACTED_LABELS.filter(([key]) => {
    const v = extracted[key];
    return v != null && String(v).trim() !== '';
  });

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <BadgeOutlinedIcon fontSize="small" color="action" />
        <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
          {fieldName || 'License verification'}
        </Typography>
        <Chip size="small" color={tone.color} icon={tone.icon} label={tone.label} />
      </Stack>

      {confidence != null && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Confidence
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, confidence))}
            color={confidence >= 80 ? 'success' : 'error'}
            sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
          />
        </Box>
      )}

      {(front || back) && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 1.5 }}>
          {[
            ['Front', front],
            ['Back', back]
          ]
            .filter(([, src]) => !!src)
            .map(([label, src]) => (
              <Box key={label as string} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  {label}
                </Typography>
                <Box
                  component="img"
                  src={src as string}
                  alt={`${fieldName || 'Licence'} ${label}`}
                  sx={{
                    width: '100%',
                    maxHeight: 140,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                  onError={(e: any) => {
                    e.target.style.display = 'none';
                  }}
                />
              </Box>
            ))}
        </Stack>
      )}

      {/* Images only exist once Jumio's callback has landed — say so rather than
          leaving an unexplained gap while the scan is still processing. */}
      {!front && !back && value.status === 'processing' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Captured images will appear once verification completes.
        </Typography>
      )}

      {name && (
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {name}
        </Typography>
      )}

      {rows.length > 0 ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 2, rowGap: 0.5 }}>
          {rows.map(([key, label]) => (
            <Box key={key} sx={{ display: 'contents' }}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                {String(extracted[key])}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No document details were extracted.
        </Typography>
      )}

      {value.message && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          {value.message}
        </Typography>
      )}
    </Box>
  );
}

/** Placeholder for a licence field with no scan yet — capture is native-only. */
export function LicenseVerificationEmpty({ fieldName }: { fieldName?: string }) {
  return (
    <Box
      sx={{
        border: '1px dashed',
        borderColor: 'grey.400',
        borderRadius: 2,
        p: 2,
        textAlign: 'center',
        bgcolor: 'grey.50'
      }}
    >
      <BadgeOutlinedIcon color="disabled" />
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {fieldName ? `${fieldName} — not captured` : 'Not captured'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        The driver scans this with the Jumio licence scan in the mobile app.
      </Typography>
    </Box>
  );
}
